import type { Directive } from 'vue';
import { useContextStore } from '@/Stores/context';

const CLASS_SCROLLED = 'is-scrolled';
const THRESHOLD = 10;
const SCROLL_HANDLER: unique symbol = Symbol('scrollHandler');

type ScrollEl = HTMLElement & { [SCROLL_HANDLER]?: () => void };

function addBaseClasses(el: HTMLElement): void {
  const contextStore = useContextStore();
  const hasTouch =
    'ontouchstart' in window ||
    (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) ||
    navigator.maxTouchPoints > 0;

  const baseClasses = ['done-js-root', 'js', hasTouch ? 'touchevents' : 'no-touchevents'];
  if (contextStore.isKite) baseClasses.push('is-kite');
  el.classList.add(...baseClasses);
}

function updateScrollState(el: HTMLElement): void {
  const y = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
  el.classList.toggle(CLASS_SCROLLED, y > THRESHOLD);
}

function isElement(node: unknown): node is Element {
  return node instanceof Element;
}

// The '.done-js-root' class is only ever added by addBaseClasses() above, always to the
// HTMLElement the vJsRoot directive is bound to -- so every element found here really is one.
function findDoneWebComponentRoot(): HTMLElement | null {
  const allEls = document.querySelectorAll('*');
  for (const el of allEls) {
    const tag = el.tagName.toLowerCase();
    if (tag.startsWith('done-')) {
      const host = el as HTMLElement;

      const inLightDom = host.querySelector<HTMLElement>('.done-js-root');
      if (inLightDom) return inLightDom;

      const shadow = host.shadowRoot;
      if (shadow) {
        const inShadow = shadow.querySelector<HTMLElement>('.done-js-root');
        if (inShadow) return inShadow;
      }
    }
  }
  return null;
}

export function findJsRoot(startEl?: Node | null): HTMLElement | null {
  let node: Node | null | undefined = startEl;

  while (node) {
    if (isElement(node)) {
      const inSameTree = node.closest<HTMLElement>('.done-js-root');
      if (inSameTree) return inSameTree;
    }

    const root = node.getRootNode?.();
    if (!(root && root instanceof ShadowRoot)) break;
    node = root.host;
  }

  const fromDocument = document.querySelector<HTMLElement>('.done-js-root');
  if (fromDocument) return fromDocument;

  const fromWebComponent = findDoneWebComponentRoot();
  if (fromWebComponent) return fromWebComponent;

  return null;
}

export const vJsRoot: Directive<HTMLElement, void> = {
  mounted(rawEl) {
    const el = rawEl as ScrollEl;
    addBaseClasses(el);

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateScrollState(el);
        ticking = false;
      });
    };

    el[SCROLL_HANDLER] = onScroll;
    window.addEventListener('scroll', onScroll, { passive: true });
    updateScrollState(el);
  },
  beforeUnmount(rawEl) {
    const el = rawEl as ScrollEl;
    const onScroll = el[SCROLL_HANDLER];
    if (onScroll) {
      window.removeEventListener('scroll', onScroll);
      el[SCROLL_HANDLER] = undefined;
    }
  },
};
